import MergeTypeRounded from "@mui/icons-material/MergeTypeRounded";
import { Box, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { CONTENT_WIDTH, FONT_SIZE, INK } from "../../../../theme/tokens";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { NurseMergeResult } from "../../../../utils/nurseMergeModels";
import { CommonHeader } from "../util/constants";
import { MergeDuplicateDialog } from "./MergeDuplicateDialog";

type Props = {
	nurse: { id: number; firstName: string; lastName: string };
	/** The profile form has edits that aren't saved — a merge would reload the record and lose them. */
	hasUnsavedEdits: boolean;
	onMerged: (result: NurseMergeResult) => void;
};

/**
 * "Merge duplicate employee" — the support-only entry point on a profile in edit mode, beside "Remove
 * Nurse". The card stays quiet; the dialog it opens is where the work happens.
 */
export const MergeDuplicateCard: FC<Props> = ({ nurse, hasUnsavedEdits, onMerged }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const [open, setOpen] = useState(false);

	return (
		<Box data-testid="profile-merge-duplicate-section" sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "start", rowGap: 1.5 }}>
			<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
				<CommonHeader>{t("nurse.merge.card.title")}</CommonHeader>
				<MetaChip label={t("nurse.merge.card.support.only")} tone="primary" />
			</Box>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY, lineHeight: 1.5, maxWidth: CONTENT_WIDTH.ANNOTATION }}>
				{t("nurse.merge.card.description", { name: formatPersonFullName(nurse, lastNameFirst) })}
			</Typography>
			<SystemButton
				data-testid="merge-employees-open-button"
				buttonVariant="secondary"
				leadingIcon={<MergeTypeRounded />}
				onClick={() => setOpen(true)}
				disabled={hasUnsavedEdits}
				noFullWidthMobile
				sx={{ width: "fit-content" }}
			>
				{t("nurse.merge.card.button")}
			</SystemButton>
			{hasUnsavedEdits && (
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }} data-testid="merge-employees-unsaved-hint">
					{t("nurse.merge.card.unsaved")}
				</Typography>
			)}
			{open && (
				<MergeDuplicateDialog
					nurse={nurse}
					entryPoint="profile"
					onClose={() => setOpen(false)}
					onMerged={(result) => {
						setOpen(false);
						onMerged(result);
					}}
				/>
			)}
		</Box>
	);
};
